import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";
import { NEUTRAL_COLOR } from "./constants";
import {
  REPORT_SCALE,
  Step,
  stepColor,
  stepFromPercentile,
  stepFromRank,
  WHITE_CELL_OUTLINE,
} from "../util/reportColors";
import { PlayerReportData, ReportSourceInfo, TeamReportProps } from "./types";
import { TeamReportApiResponse } from "../util/Requests";
import { OverviewPageProps } from "./OverviewPage/types";
import { BasicTeamSectionPageProps } from "./BasicTeamSectionPage/types";
import { transformSourceGameLog } from "./GameLogItem/utils";
import { transformAdvancedStats } from "./PlayerPage/AdvancedStatsSection/utils";
import { TeamReportDataTableProps, TeamReportDataTableRow } from "./TeamReportDataTable/types";
import { ShotQueryContext } from "./ShotDetailModal/types";

export function getRankPercentileText(
  rank?: number,
  percentile?: number
): string {
  if (percentile !== undefined && percentile !== null) {
    return `${percentile.toFixed(0)}`;
  } else if (rank !== undefined && rank !== null) {
    return `${nth(rank)}`;
  } else {
    return "";
  }
}

export function rankToPercentile(
  rank: number,
  rankPopulation: number = 30
): number {
  return ((rankPopulation - rank) / rankPopulation) * 100;
}

export function getPercentileFromRankOrPercentile(
  percentile?: number,
  rank?: number,
  rankPopulation?: number
): number | undefined {
  if (percentile !== undefined && percentile !== null) {
    return percentile;
  } else if (rank !== undefined && rank !== null) {
    return rankToPercentile(rank, rankPopulation);
  }
}

/** The bucket a ramp value falls in (null without a value). A percentile wins over a rank, as in
 * getPercentileFromRankOrPercentile. */
function rampStep(
  percentile?: number,
  rank?: number,
  rankPopulation: number = 30
): Step | null {
  if (percentile !== undefined && percentile !== null)
    return stepFromPercentile(percentile);
  return rank !== undefined ? stepFromRank(rank, rankPopulation) : null;
}

/** The report ramp (util/reportColors): seven buckets of the red / green scale with a white middle. A rank (with no
 * percentile) buckets on the rank itself, ranks 1-5, 6-10, … of `rankPopulation`. */
export function getColorFromPercentile(
  percentile?: number,
  rank?: number,
  rankPopulation: number = 30
): string {
  const step = rampStep(percentile, rank, rankPopulation);
  return step === null ? NEUTRAL_COLOR : stepColor(step);
}

/**
 * The ring around a cell that lands in the white middle bucket, as a box-shadow, else undefined. Inset, so it takes
 * no space: no cell moves for it.
 */
export function getRampOutline(
  percentile?: number,
  rank?: number,
  rankPopulation: number = 30
): string | undefined {
  return rampStep(percentile, rank, rankPopulation) === 0
    ? WHITE_CELL_OUTLINE
    : undefined;
}

/** The rank a cell's colour comes from: only when it has no percentile (a percentile wins, as it does above). */
export const colorRank = (
  percentile?: number | null,
  rank?: number | null
): number | undefined =>
  percentile === undefined || percentile === null
    ? rank ?? undefined
    : undefined;

export function getColorTS(
  tsPercent?: number,
  context: "offense" | "defense" = "offense"
): string {
  if (tsPercent === undefined || tsPercent === null) {
    return NEUTRAL_COLOR;
  }
  const { bad: BAD_COLOR, good: GOOD_COLOR } = REPORT_SCALE;

  // For defense, flip the color logic - higher TS% is bad, lower TS% is good
  if (context === "defense") {
    if (tsPercent <= 50) {
      return GOOD_COLOR; // Very good defense (low opponent TS%)
    } else if (tsPercent <= 52) {
      return alpha(GOOD_COLOR, 0.7); // Good defense
    } else if (tsPercent <= 55) {
      return alpha(GOOD_COLOR, 0.4); // Above average defense
    } else if (tsPercent <= 58) {
      return NEUTRAL_COLOR; // Average defense
    } else if (tsPercent <= 61) {
      return alpha(BAD_COLOR, 0.4); // Below average defense
    } else if (tsPercent <= 64) {
      return alpha(BAD_COLOR, 0.7); // Poor defense
    } else {
      return BAD_COLOR; // Very poor defense (high opponent TS%)
    }
  }

  // Default offense logic (unchanged)
  if (tsPercent <= 50) {
    return BAD_COLOR; // Very poor
  } else if (tsPercent <= 52) {
    return alpha(BAD_COLOR, 0.7); // Poor
  } else if (tsPercent <= 55) {
    return alpha(BAD_COLOR, 0.4); // Below average
  } else if (tsPercent <= 58) {
    return NEUTRAL_COLOR; // Average
  } else if (tsPercent <= 61) {
    return alpha(GOOD_COLOR, 0.4); // Above average
  } else if (tsPercent <= 64) {
    return alpha(GOOD_COLOR, 0.7); // Great
  } else {
    return GOOD_COLOR; // Elite (≥ 67%)
  }
}

export const range = (count: number) => Array.from(Array(count).keys());

/** A report's season type for its scope lines: the header's season_type ("playoff", "preseason", else the
 * regular season). */
export const seasonTypeLabel = (seasonType?: string) =>
  seasonType === "playoff"
    ? "Playoffs"
    : seasonType === "preseason"
      ? "Preseason"
      : "Regular Season";

export const nth = (n: number, isTied?: boolean): string => {
  const prefix = isTied ? "T-" : "";
  switch (n % 10) {
    case 1:
      if (n % 100 === 11) {
        return `${prefix}${n}th`;
      }
      return `${prefix}${n}st`;
    case 2:
      if (n % 100 === 12) {
        return `${prefix}${n}th`;
      }
      return `${prefix}${n}nd`;
    case 3:
      if (n % 100 === 13) {
        return `${prefix}${n}th`;
      }
      return `${prefix}${n}rd`;
    default:
      return `${prefix}${n}th`;
  }
};

export function mapPlayerDataToPlayerPageProps(player: PlayerReportData) {
  return {
    playerHeaderProps: {
      playerId: player.playerInfo.playerId,
      firstName: player.playerInfo.firstName,
      lastName: player.playerInfo.lastName,
      age: player.playerInfo.age,
      position: player.playerInfo.position,
      jerseyNumber: player.playerInfo.jerseyNumber,
      height: player.playerInfo.height,
      weight: player.playerInfo.weight,
      shotChartProps: {
        data: player.shotChart,
        width: 280,
      },
    },
    waydSectionProps: {
      offenseImpactData: player.sections.WAYD.offenseImpactData,
      defenseImpactData: player.sections.WAYD.defenseImpactData,
      offenseFrequencyData: player.sections.WAYD.offenseFrequencyData,
      defenseFrequencyData: player.sections.WAYD.defenseFrequencyMetrics,
      valueMetrics: [], // TODO: update when data payload is updated with this data
    },
    advancedStatsProps: transformAdvancedStats(
      player.sections["Advanced Stats"].rows
    ),
    actionsTableProps: {
      headers: player.sections["Halfcourt Offensive Actions"].headers,
      rows: player.sections["Halfcourt Offensive Actions"].rows || [],
    },
    closeoutsTableProps: {
      headers: player.sections["Closeout Opportunities"].headers,
      rows:
        player.sections["Closeout Opportunities"].rows?.map((row) => {
          if (row.name === "other") {
            return {
              ...row,
              name: "Hold",
            };
          }
          return row;
        }) ?? [],
    },
  };
}

// Transform API response to TeamReport component props
export function transformTeamReportApiResponse(
  apiResponse: TeamReportApiResponse,
  availableGameIds: string[],
  teamName?: string,
  teamNickname?: string,
  teamAbbrev?: string,
  season?: number,
  lastSaved?: string
): TeamReportProps {
  const overviewSection = apiResponse.overview?.sections || {};
  const header = apiResponse.header || {};

  // Inject rankPopulation from header into every table cell so color scaling
  // is correct for playoffs (20 teams) vs regular season (30 teams).
  const rankPop = header.rankPopulation;
  const stampCells = (obj: Record<string, any> | undefined) => {
    for (const cell of Object.values(obj ?? {})) {
      if (cell && typeof cell === "object" && "rank" in cell && !("rankPopulation" in cell)) {
        (cell as any).rankPopulation = rankPop;
      }
    }
  };
  const injectRankPopulation = (sections: Record<string, any>) => {
    if (!rankPop || rankPop === 30) return sections;
    for (const section of Object.values(sections)) {
      for (const row of section?.rows ?? []) {
        stampCells(row?.cells);
        stampCells(row?.shotDistribution);
        const subRowLists = row?.subRowsByType
          ? Object.values(row.subRowsByType)
          : [row?.subRows ?? []];
        for (const subRowList of subRowLists) {
          for (const subRow of subRowList as any[]) {
            stampCells(subRow?.cells);
            stampCells(subRow?.shotDistribution);
          }
        }
      }
    }
    return sections;
  };

  // Transform Overview Page Props with safety checks
  const overviewPageProps: OverviewPageProps = {
    headerProps: {
      teamId: header.teamId,
      teamName: teamName || "",
      teamNickname: teamNickname || "",
      teamAbbrev: teamAbbrev || "",
      season,
      season_type: header.season_type,
      conferenceStandingsActual: header.conferenceStandingsActual,
      conferenceStandingsExpected: header.conferenceStandingsExpected,
      gameInfo: header.gameInfo,
      game: header.game,
      asOfDate: lastSaved ? dayjs(lastSaved).format("MMM D, YYYY") : undefined,
      feed: header.feed ?? null,
      delayedFeedGames: Array.isArray(header.delayedFeedGames) ? header.delayedFeedGames : [],
      window: header.window ?? null,
    },
    rankingsProps: {
      hcOffense: {
        label: "Offense",
        rankOrPercentile: overviewSection.Breakdown?.["HC Offense"],
        rankPopulation: header.rankPopulation ?? 30,
      },
      hcDefense: {
        label: "Defense",
        rankOrPercentile: overviewSection.Breakdown?.["HC Defense"],
        rankPopulation: header.rankPopulation ?? 30,
      },
      trOffense: {
        label: "Off + DRB",
        rankOrPercentile: overviewSection.Breakdown?.["DRB / Trans O"],
        rankPopulation: header.rankPopulation ?? 30,
      },
      trDefense: {
        label: "Def + ORB",
        rankOrPercentile: overviewSection.Breakdown?.["ORB / Trans D"],
        rankPopulation: header.rankPopulation ?? 30,
      },
    },
    teamOverviewTableData: overviewSection.Overall || { headers: [], rows: [] },
    playerRotations: {
      players: overviewSection.Rotation || [],
    },
    gameLog: (overviewSection["Game Log"] || []).map(transformSourceGameLog),
    // Overview Team Profile strip (2026-09-10; game blobs too since TR4)
    teamProfile: (overviewSection as any)["Team Profile"],
  };

  // Transform Halfcourt Offense Page Props with safety checks
  injectRankPopulation(overviewSection);

  const hcOffenseSections = addQueryContextToSections(
    apiResponse.hc_offense?.sections || {},
    "HC_OFFENSE",
    "offense"
  );
  injectRankPopulation(hcOffenseSections);
  const halfcourtOffensePageProps: BasicTeamSectionPageProps = {
    sections: hcOffenseSections,
    // Blobs list the assisted table as "Assisted vs unassisted" but key it "Assisted v Unassisted", so the coaches'
    // page never found it (the ownership view reads the key directly); read the list's name as the key
    // (TR6, 2026-10-10; the backend's list is fixed for blobs built after)
    sectionNames: apiResponse.hc_offense?.sectionNames?.map((name: string) =>
      name === "Assisted vs unassisted" ? "Assisted v Unassisted" : name,
    ),
    title: apiResponse.hc_offense?.title || "First-Shot Halfcourt Offense",
    // Blob shot-profile tables render at the top of the page beside the
    // hexbin (replaced the possession-share bar charts, user call 2026-09-10)
    shotProfile: (apiResponse as any).shot_profile?.offense,
    shotDistributionContext: "offense",
    teamAbbrev: teamAbbrev || undefined,
  };

  const hcDefenseSections = addQueryContextToSections(
    apiResponse.hc_defense?.sections || {},
    "HC_DEFENSE",
    "defense"
  );
  injectRankPopulation(hcDefenseSections);
  const halfcourtDefensePageProps: BasicTeamSectionPageProps = {
    sections: hcDefenseSections,
    sectionNames: apiResponse.hc_defense?.sectionNames,
    title: apiResponse.hc_defense?.title || "First-Shot Halfcourt Defense",
    shotProfile: (apiResponse as any).shot_profile?.defense,
    shotDistributionContext: "defense",
    teamAbbrev: teamAbbrev || undefined,
  };

  const transOffenseSections = addQueryContextToSections(
    apiResponse.dreb_trans_o?.sections || {},
    "TRANSITION_OFFENSE",
    "offense"
  );
  injectRankPopulation(transOffenseSections);
  const transitionOffensePageProps: BasicTeamSectionPageProps = {
    sections: transOffenseSections,
    sectionNames: apiResponse.dreb_trans_o?.sectionNames,
    title: apiResponse.dreb_trans_o?.title || "Transition Offense",
    shotDistributionContext: "offense",
    teamAbbrev: teamAbbrev || undefined,
  };

  const transDefenseSections = addQueryContextToSections(
    apiResponse.oreb_trans_d?.sections || {},
    "TRANSITION_DEFENSE",
    "defense"
  );
  injectRankPopulation(transDefenseSections);
  const transitionDefensePageProps: BasicTeamSectionPageProps = {
    sections: transDefenseSections,
    sectionNames: apiResponse.oreb_trans_d?.sectionNames,
    title: apiResponse.oreb_trans_d?.title || "Transition Defense",
    shotDistributionContext: "defense",
    teamAbbrev: teamAbbrev || undefined,
  };

  const reportSource = readReportSource(apiResponse);
  if (reportSource.scope || reportSource.source) {
    for (const page of [
      halfcourtOffensePageProps,
      halfcourtDefensePageProps,
      transitionOffensePageProps,
      transitionDefensePageProps,
    ]) {
      stampReportSource(page.sections, reportSource);
      page.reportSource = reportSource;
    }
    stampReportSource(overviewSection, reportSource);
  }

  return {
    overviewPageProps,
    halfcourtOffensePageProps,
    halfcourtDefensePageProps,
    transitionOffensePageProps,
    transitionDefensePageProps,
    players: apiResponse.players || [],
    availableGameIds,
    reportSource,
    gameShotProfile: (apiResponse as any).game_shot_profile ?? null,
    previewStatsSeason: (apiResponse as any).placeholder?.statsSeason ?? null,
    guardLineups: (apiResponse as any).guard_lineups ?? null,
  };
}

/** The blob's `scope` / `source` (R1); both null on reports built before R1. */
export function readReportSource(apiResponse: any): ReportSourceInfo {
  const scope = apiResponse?.scope;
  const source = apiResponse?.source;
  return {
    scope: scope === "nba" || scope === "draft" ? scope : null,
    source: source === "ss" || source === "ctg" || source === "skill" ? source : null,
  };
}

// Team reports need the new season's games, so they stay on the prior season
// until its regular season opens (2026-27: October 20). Rotations keeps the
// October 1 rule above, since it shows the upcoming preseason schedule.
export function getNBAReportSeasonYear(): number {
  const now = dayjs();
  const opened = now.month() > 9 || (now.month() === 9 && now.date() >= 20);
  return opened ? now.year() : now.year() - 1;
}

/**
 * Put the report's scope / source on every row drill-down context, so a shot
 * chart, action chart or playlist opened from a row reads the provider the
 * report was built from (F1 contract §1b). Mutates in place: the sections were
 * already copied by the transform.
 */
function stampReportSource(node: unknown, info: ReportSourceInfo): void {
  if (Array.isArray(node)) {
    for (const item of node) stampReportSource(item, info);
    return;
  }
  if (!node || typeof node !== "object") return;
  const obj = node as Record<string, any>;
  for (const [key, value] of Object.entries(obj)) {
    if (key === "queryContext" && value && typeof value === "object") {
      obj.queryContext = { ...value, scope: info.scope, source: info.source };
    } else if (value && typeof value === "object") {
      // Sections, rows, subRows, subRowsByType variants and sub-tables alike.
      stampReportSource(value, info);
    }
  }
}


/**
 * Helper to extract row name as a string for queryContext filters.
 */
function getRowNameString(name: React.ReactNode): string {
  if (typeof name === "string") return name;
  if (typeof name === "number") return String(name);
  // For React elements, return empty string (backend should provide full context)
  return "";
}

/**
 * Adds queryContext to each row in the table data.
 * This allows shot distribution bars to be clickable.
 */
function addQueryContextToRows(
  tableData: TeamReportDataTableProps,
  table: string,
  section: string,
  perspective: "offense" | "defense"
): TeamReportDataTableProps {
  if (!tableData?.rows) return tableData;

  const addContextToRow = (
    row: TeamReportDataTableRow,
    parentRowName?: string
  ): TeamReportDataTableRow => {
    // Skip if queryContext already exists (backend provided it)
    if (row.queryContext) return row;

    const rowName = getRowNameString(row.name);
    const filters: [string, string, string[]][] = [];

    // Add row name as filter if it's meaningful
    if (rowName && rowName.toLowerCase() !== "overall") {
      filters.push(["row_name", "eq", [rowName]]);
    }

    // If this is a sub-row, also include parent row name
    if (parentRowName && parentRowName.toLowerCase() !== "overall") {
      filters.push(["parent_row_name", "eq", [parentRowName]]);
    }

    const queryContext: ShotQueryContext = {
      table,
      section,
      perspective,
      filters,
    };

    // Process sub-rows recursively
    const subRows = row.subRows?.map((subRow) =>
      addContextToRow(subRow, rowName)
    );

    return {
      ...row,
      queryContext,
      subRows,
    };
  };

  return {
    ...tableData,
    rows: tableData.rows.map((row) => addContextToRow(row)),
  };
}

/**
 * Adds queryContext to all sections in the page props.
 */
function addQueryContextToSections(
  sections: { [key: string]: TeamReportDataTableProps },
  table: string,
  perspective: "offense" | "defense"
): { [key: string]: TeamReportDataTableProps } {
  const result: { [key: string]: TeamReportDataTableProps } = {};

  for (const [sectionName, tableData] of Object.entries(sections)) {
    result[sectionName] = addQueryContextToRows(
      tableData,
      table,
      sectionName,
      perspective
    );
  }

  return result;
}
